import React, { useContext, useEffect, useMemo } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Context as AlertContext } from 'contexts/alert';
import { useForm } from 'react-hook-form';
import { Input } from 'components/formInputs/input';
import { Config } from 'configs';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PageTitle } from 'components/layouts/pageTitle';

export const CompanyEditForm = ({ itemData }) => {
  const { editById } = useContext(CompanyContext);
  const { state: auth, onCompanyUpdate } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);

  const schema = yup.object().shape({
    _id: yup.string().default(itemData?._id),
    name: yup.string().required('Please enter a company name.').default(itemData?.name),
    address: yup.string().default(itemData?.address),
    company_code: yup.string().default(itemData?.company_code),
    vat: yup.string().default(itemData?.vat),
    default_vat_size: yup.number()
      // eslint-disable-next-line no-restricted-globals
      .transform((value) => (isNaN(value) ? 0 : value))
      .nullable()
      .default(itemData?.default_vat_size || undefined),
  });

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset(itemData);
  }, [itemData, reset]);

  const onSubmit = async (data) => {
    const sumbitData = {
      name: data.name,
      address: data.address,
      company_code: data.company_code,
      vat: data.vat,
      default_vat_size: data.default_vat_size,
    };

    await editById(
      {
        collectionName: Config.DATA.COLLECTIONS.COMPANY,
        id: data._id,
        updateClause: { $set: sumbitData },
        token: auth.token,
        resourceV2: 'AdminCompany',
        updateTypeV2: 'General',
        updateDataV2: sumbitData,
        companyIdForV2: auth.company?.company,
      },
      () => {
        onCompanyUpdate(sumbitData);
        alertController.success({ text: 'Account settings updated' });
      },
    );
  };
  const onError = (data) => console.log(data);

  return (
    <>
      <PageTitle
        title="Company"
        breadcrumbs={[{ path: '/account', title: 'Account settings' }]}
        isTopLevel
      />
      <div className="container mx-auto px-2 md:px-6 lg:px-8">
        <div className="w-full">
          <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="bg-white py-6 px-4 space-y-6 sm:p-6">

                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Name*
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <Input
                        {...register('name')}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        id="name"
                        type="text"
                        errorMessage={errors?.name}
                      />
                    </div>
                    {errors?.name && (
                      <p className="mt-2 text-sm text-red-600" id="name-error">
                        {errors?.name.message}
                      </p>
                    )}
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                      Address
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <Input
                        {...register('address')}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        id="address"
                        type="text"
                        errorMessage={errors?.address}
                      />
                    </div>
                    {errors?.address && (
                      <p className="mt-2 text-sm text-red-600" id="address-error">
                        {errors?.address.message}
                      </p>
                    )}
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="company_code" className="block text-sm font-medium text-gray-700">
                      Company code
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <Input
                        {...register('company_code')}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        id="company_code"
                        type="text"
                        errorMessage={errors?.company_code}
                      />
                    </div>
                    {errors?.company_code && (
                      <p className="mt-2 text-sm text-red-600" id="company_code-error">
                        {errors?.company_code.message}
                      </p>
                    )}
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="vat" className="block text-sm font-medium text-gray-700">
                      VAT
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <Input
                        {...register('vat')}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        id="vat"
                        type="text"
                        errorMessage={errors?.vat}
                      />
                    </div>
                    {errors?.vat && (
                      <p className="mt-2 text-sm text-red-600" id="vat-error">
                        {errors?.vat.message}
                      </p>
                    )}
                  </div>
                  {/* <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="default_vat_size" className="block text-sm font-medium text-gray-700">
                      Default VAT size (%)
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <Input
                        {...register('default_vat_size')}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        id="default_vat_size"
                        type="text"
                        errorMessage={errors?.default_vat_size}
                      />
                    </div>
                    {errors?.default_vat_size && (
                      <p className="mt-2 text-sm text-red-600" id="default_vat_size-error">
                        {errors?.default_vat_size.message}
                      </p>
                    )}
                  </div> */}
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  type="submit"
                  className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
